import { getAnnouncementAttachment } from "../api/announcements.ts";

/** Mirrors `MaxAnnouncementAttachmentBytes` on the server. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** "4.8 MB", "812 KB", "0 B". */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** What is wrong with a file chosen for upload, or null. The server checks the content again; this is for a quick answer. */
export function attachmentProblem(file: Pick<File, "name" | "size">): string | null {
  if (!/\.pdf$/i.test(file.name)) return "Only PDF files can be attached.";
  if (file.size <= 0) return "That file is empty.";
  if (file.size > MAX_ATTACHMENT_BYTES) return `The PDF can be at most ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB (this one is ${formatFileSize(file.size)}).`;
  return null;
}

/** The file's bytes as base64, without the `data:` prefix. Goes through FileReader so a few MB never become one huge string of chars. */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read."));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/** Base64 → bytes, in slices so a 10 MB file does not build one giant binary string. */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 8192) {
    const end = Math.min(i + 8192, binary.length);
    for (let j = i; j < end; j++) bytes[j] = binary.charCodeAt(j);
  }
  return bytes;
}

/**
 * Fetches an announcement's PDF for showing on the page. The file sits behind the session (an `<iframe src>` or `<a href>` cannot
 * send the Auth header), so it is fetched and handed to the viewer as a blob URL. The caller owns the URL and revokes it
 * (`URL.revokeObjectURL`) when it stops showing the file. Resolves to the URL and file name, or an error message.
 */
export async function loadAnnouncementPdf(announcementId: number): Promise<{ url: string; fileName: string } | { error: string }> {
  try {
    const r = await getAnnouncementAttachment(announcementId);
    if (r.bError || !r.oData?.ContentBase64) return { error: r.sMessage || "The file could not be opened." };
    const blob = new Blob([base64ToBytes(r.oData.ContentBase64)], { type: "application/pdf" });
    return { url: URL.createObjectURL(blob), fileName: r.oData.FileName || "announcement.pdf" };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "The file could not be opened." };
  }
}
